import React from 'react';
import { IconSizes } from '@newcore/icons';
import { FontWeights } from '@newcore/ui/enums/font-weights';
import { DisplaySizes } from '@newcore/ui/enums/typography-display-sizes';
import { TextSizes } from '@newcore/ui/enums/typography-text-sizes';
import { Spinner } from '@newcore/ui/loading';
import { Display, Text } from '@newcore/ui/typography';
import { InvitationStatus } from './types';

/**
 * Styled components and templates for the invitation router
 */

interface StatusContentProps {
  status: InvitationStatus;
  countdown: number;
  onContinueInBrowser: () => void;
}

interface StatusDisplayProps {
  title: string;
  subtitle: string;
  showCountdown?: boolean;
  countdown?: number;
}

/**
 * Generic status display component
 */
const StatusDisplay: React.FC<StatusDisplayProps> = ({
  title,
  subtitle,
  showCountdown = false,
  countdown = 0,
}) => (
  <div className='text-center'>
    <Spinner size={IconSizes.LG} />
    <Text as='p' size={TextSizes.MD} className='mt-4 text-secondary'>
      {title}
    </Text>
    {showCountdown && (
      <Text as='p' size={TextSizes.SM} className='mt-2 text-secondary'>
        {subtitle.replace('{countdown}', countdown.toString())}
      </Text>
    )}
    {!showCountdown && subtitle && (
      <Text as='p' size={TextSizes.SM} className='mt-2 text-secondary'>
        {subtitle}
      </Text>
    )}
  </div>
);

/**
 * Content component that renders different states based on status
 */
export const StatusContent: React.FC<StatusContentProps> = ({
  status,
  countdown,
  onContinueInBrowser,
}) => {
  const getStatusConfig = () => {
    switch (status) {
      case InvitationStatus.DETECTING:
        return {
          title: 'Setting up your invitation...',
          subtitle: '',
          showCountdown: false,
        };

      case InvitationStatus.ROUTING:
        return {
          title: 'Opening NewCore app...',
          subtitle: "Redirecting in {countdown} seconds if app doesn't open",
          showCountdown: true,
        };

      case InvitationStatus.FALLBACK:
        return {
          title: 'Opening in browser...',
          subtitle: "Don't have the NewCore app? Continue in your browser.",
          showCountdown: false,
        };

      default:
        return {
          title: 'Processing invitation...',
          subtitle: '',
          showCountdown: false,
        };
    }
  };

  const config = getStatusConfig();

  return (
    <>
      <StatusDisplay
        title={config.title}
        subtitle={config.subtitle}
        showCountdown={config.showCountdown}
        countdown={countdown}
      />

      {status === InvitationStatus.ROUTING && (
        <div className='mt-6 text-center'>
          <button
            onClick={onContinueInBrowser}
            className='underline focus:outline-none focus:ring-2 focus:ring-brand-secondary focus:ring-opacity-50 rounded'
          >
            <Text
              size={TextSizes.SM}
              className='text-brand-secondary hover:text-brand-secondary_hover'
            >
              Continue in browser instead
            </Text>
          </button>
        </div>
      )}
    </>
  );
};

/**
 * Main container component with consistent styling
 */
export const InvitationContainer: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => (
  <div className='min-h-screen flex items-center justify-center bg-secondary'>
    <div className='max-w-md w-full mx-4'>
      <div className='bg-white rounded-lg shadow-lg p-8'>
        <div className='text-center mb-6'>
          <Display
            as='h1'
            size={DisplaySizes.XS}
            weight={FontWeights.BOLD}
            className='text-primary'
          >
            Welcome to NewCore!
          </Display>
          <Text as='p' size={TextSizes.MD} className='mt-2 text-secondary'>
            We&apos;re setting up your secure access
          </Text>
        </div>
        {children}
      </div>
    </div>
  </div>
);
